// Build version accuracy for the PWA update flow.
//
// The old "Update available" prompt fired whenever a service worker was in the
// `waiting` state — which is ALSO true right after a hard refresh that already
// loaded the latest code, so it showed a false, sticky toast. These helpers let
// every update prompt gate on a REAL difference between the running build and
// the deployed build.

/** Build id baked into this bundle at build time (vite define). */
export const APP_VERSION: string =
    (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';

let cached: { version: string | null; at: number } | null = null;
const TTL_MS = 10_000; // brief cache so repeated checks don't refetch per route change

/**
 * The build id currently DEPLOYED, read from /version.json (network, no-store).
 * Returns null when it can't be determined (offline, missing file, parse error).
 */
export async function getDeployedVersion(force = false): Promise<string | null> {
    if (!force && cached && Date.now() - cached.at < TTL_MS) return cached.version;
    try {
        const res = await fetch(`/version.json?_=${Date.now()}`, {
            cache: 'no-store',
            headers: { 'cache-control': 'no-cache' },
        });
        if (!res.ok) { cached = { version: null, at: Date.now() }; return null; }
        const data = await res.json().catch(() => null);
        const version = data && typeof data.version === 'string' ? data.version : null;
        cached = { version, at: Date.now() };
        return version;
    } catch {
        cached = { version: null, at: Date.now() };
        return null;
    }
}

/**
 * True only when a DIFFERENT build has been deployed than the one running.
 * Biased to false whenever the deployed version is unknown, so we never nag
 * with a false "update available".
 */
export async function isUpdateReady(force = false): Promise<boolean> {
    const deployed = await getDeployedVersion(force);
    if (!deployed) return false;
    return deployed !== APP_VERSION;
}

const FORCE_REFRESH_FLAG = "nk_forced_refresh_at";
// Minimum gap between two auto-refreshes in ONE tab session (reload-loop guard).
const REFRESH_COOLDOWN_MS = 90_000;

// Absolute failsafe: navigate even if getRegistrations/unregister hang (wedged
// iOS). Cache-API deletes are fire-and-forget — they are NOT required for
// correctness once the worker is gone (Cache Storage isn't used without a SW
// fetch handler), and awaiting them on a large mobile precache was the
// "tap update, nothing for 30–40s" bug.
const HARD_REFRESH_FAILSAFE_MS = 1200;

function navigateCacheBusted(): void {
    const url = new URL(window.location.href);
    url.searchParams.set("_u", Date.now().toString());
    window.location.replace(url.toString());
}

/**
 * SW-INDEPENDENT hard refresh onto the latest deploy — the bulletproof reload
 * shared by the explicit "Update available" tap (useAppUpdate) and the
 * scorched-earth launch backstop below.
 *
 * Why not a plain `location.reload()`: on a wedged iOS home-screen PWA a reload
 * re-serves the SW-PRECACHED (stale) index.html — or WebKit's own cached
 * document — so the running build never changes ("I tap update and nothing
 * happens"). Instead we nudge any waiting worker, unregister EVERY worker, drop
 * all Cache-API caches (best-effort, non-blocking), then HARD-NAVIGATE to a
 * cache-busted URL: the novel query string defeats WebKit's document cache and,
 * with no worker left to intercept, the navigation genuinely hits the network
 * and pulls the latest index.html (which references the new hashed chunks).
 * Auth survives — only the SW + Cache API are cleared, never
 * localStorage/IndexedDB — so the Supabase session persists. `_u` is ignored by
 * react-router and dropped on the next in-app navigation.
 *
 * Speed: cache deletes never block navigation (that was the 30–40s mobile hang).
 * Unregister is awaited (normally milliseconds) so the next nav isn't served the
 * precached shell; an absolute failsafe (~1.2s) navigates even if SW APIs hang —
 * so a tap always produces a visible refresh within ~1s on every surface
 * (browser, mobile web, PWA).
 */
export async function hardRefreshToLatest(): Promise<void> {
    let navigated = false;
    const go = () => {
        if (navigated) return;
        navigated = true;
        navigateCacheBusted();
    };
    const failsafe = window.setTimeout(go, HARD_REFRESH_FAILSAFE_MS);

    try {
        // Clear one-shot auto-heal flags so a real recovery doesn't look like a loop.
        try {
            sessionStorage.removeItem("nk_chunk_retry_reload_at");
            sessionStorage.removeItem("noknok.errorboundary.autoreload.v1");
        } catch { /* ignore */ }

        // Ask the controlling worker to tear itself down + navigate us (fast path
        // when the SW is healthy). Fire-and-forget — we still do the page-side
        // unregister + navigate below.
        try {
            navigator.serviceWorker?.controller?.postMessage({ type: "FORCE_REFRESH" });
        } catch { /* ignore */ }

        // Cache nuke: start immediately, never block navigation on it.
        if ("caches" in window) {
            void caches.keys()
                .then((keys) => Promise.all(keys.map((k) => caches.delete(k).catch(() => false))))
                .catch(() => { /* ignore */ });
        }

        // Unregister every worker — this is what stops the next navigation from
        // being served the precached shell. Normally completes in ms; the failsafe
        // above covers a hang.
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(async (r) => {
                try { r.waiting?.postMessage({ type: "SKIP_WAITING" }); } catch { /* ignore */ }
                try { r.active?.postMessage({ type: "FORCE_REFRESH" }); } catch { /* ignore */ }
                await r.unregister().catch(() => false);
            }));
        }
    } catch { /* best effort — navigate regardless */ }

    window.clearTimeout(failsafe);
    go();
}

/**
 * Launch-time freshness backstop — the SW-INDEPENDENT path that keeps the whole
 * installed base current WITHOUT a reinstall, especially iOS home-screen PWAs
 * (which are unreliable about ever updating the service worker on their own).
 *
 * Runs on every cold launch / full load (main.tsx, fire-and-forget). If the
 * running build differs from the deployed one:
 *   1. SURGICAL — a newer worker is already `waiting`: skip-waiting + reload.
 *      Cheap: only the chunks that changed in the deploy download.
 *   2. NUDGE — ask the SW to fetch a new worker, TIMEOUT-guarded so a wedged
 *      worker can't hang this, then retry the surgical path.
 *   3. SCORCHED-EARTH — still nothing waiting ⇒ the SW lifecycle is stuck (the
 *      iOS wedge). Unregister every worker + drop all Cache-API caches + reload
 *      so the fetch pulls genuinely fresh code. Zero dependency on the SW.
 *
 * Loop-safe: at most one refresh per ~90s per tab session, and at most one
 * scorched-earth attempt per deployed version per session — a persistent
 * mismatch (e.g. a CDN mid-deploy race) falls through to the passive "Update
 * available" notification instead of reloading again. Auth survives: only the
 * Cache API + SW are cleared, never localStorage/IndexedDB, so the Supabase
 * session stays put. Returns true when it kicked off a reload.
 */
export async function forceFreshIfStale(): Promise<boolean> {
    if (typeof window === "undefined") return false;

    let deployed: string | null = null;
    try {
        deployed = await getDeployedVersion(true);
    } catch {
        return false;
    }
    // Current, or an unknown/flaky read → do nothing (never nag on a bad check).
    if (!deployed || deployed === APP_VERSION) {
        try { sessionStorage.removeItem(FORCE_REFRESH_FLAG); } catch { /* ignore */ }
        return false;
    }

    // ── Reload-loop guards (sessionStorage survives a reload, resets on close) ──
    // We CLAIM the attempt up front, before any await/reload, so a duplicate or
    // re-entrant invocation (dev HMR, StrictMode, a racing reload) can't slip
    // through the window and double-fire. Result: at most ONE auto-refresh per
    // deployed version per tab session, and never within 90s of the last one.
    let canGuard = true;
    const triedKey = `nk_update_tried_${deployed}`;
    try {
        const last = Number(sessionStorage.getItem(FORCE_REFRESH_FLAG) || "0");
        if (last && Date.now() - last < REFRESH_COOLDOWN_MS) return false;
        if (sessionStorage.getItem(triedKey) === "1") return false;
        // Claim it now.
        sessionStorage.setItem(FORCE_REFRESH_FLAG, String(Date.now()));
        sessionStorage.setItem(triedKey, "1");
    } catch { canGuard = false; }

    const getReg = (): Promise<ServiceWorkerRegistration | null> =>
        "serviceWorker" in navigator
            ? navigator.serviceWorker.getRegistration().then(r => r ?? null).catch(() => null)
            : Promise.resolve(null);

    // 1. Surgical — a newer worker is already waiting.
    const reg = await getReg();
    if (reg?.waiting) {
        console.info(`[app-version] stale ${APP_VERSION} → ${deployed}: activating waiting worker`);
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
        setTimeout(() => window.location.reload(), 1200);
        return true;
    }

    // 2. Nudge the SW to fetch a new worker — never hang on it.
    try {
        await Promise.race([
            reg?.update() ?? Promise.resolve(),
            new Promise((resolve) => setTimeout(resolve, 4000)),
        ]);
    } catch { /* offline / flaky */ }
    const reg2 = await getReg();
    if (reg2?.waiting) {
        console.info(`[app-version] stale ${APP_VERSION} → ${deployed}: activating fetched worker`);
        reg2.waiting.postMessage({ type: "SKIP_WAITING" });
        setTimeout(() => window.location.reload(), 1200);
        return true;
    }

    // 3. Scorched-earth — the SW is stuck. Skip if we couldn't set the guards
    //    (sessionStorage unavailable) so we can never loop.
    if (!canGuard) return false;
    console.warn(`[app-version] stale ${APP_VERSION} → ${deployed}: SW stuck — scorched-earth refresh`);
    // Cache-busted hard navigation (not a plain reload) so a wedged iOS PWA
    // can't re-serve the stale precached shell. See hardRefreshToLatest.
    await hardRefreshToLatest();
    return true;
}
